import React, { Fragment, useMemo, useCallback } from "react";
import { Field, Form, withFormik } from "formik";
import TextInput from "./TextInput";
import CheckboxInput from "./CheckboxInput";
import ImagesInput from "./ImagesInput";
import Button from "./CustomButton";
import { getBreakpointFromWidth } from "../helpers/index";
import { Alert } from "reactstrap";
import Icon from "@fortawesome/react-fontawesome";
import _ from "lodash";
import Table from "./Table";
import VariantsColumns from "./VariantsColumns";
import * as Yup from "yup";
import Loading from "./Loading";
import AddVariantButton from "./AddVariantButton";
import CategoryInput from "./CategoryInput";

const initialValues = {
  name: "",
  description: "",
  price: "",
  images: [],
  has_tax: false,
  categories: [],
  variants: []
}

const setUpdateValues = (product) => {
  product.arrImages = product.images;
  return {
    name: product.name,
    description: product.description,
    price: product.price,
    images: product.arrImages,
    has_tax: product.has_tax,
    categories: product.category_products,
    variants: product.variants
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  description: Yup.string().required('Required'),
  price: Yup.number()
    .min(0, 'Cannot be less than zero')
    .required('Required')
    .typeError('Invalid number'),
  has_tax: Yup.boolean(),
  images: Yup.array().min(1, 'At least one image'),
  variants: Yup.array(),
  categories: Yup.array().min(1, 'At least one category')
})

const ProductForm = withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ action, product = null }) => {
      if(action === "update")
        return setUpdateValues(product);
      else
        return initialValues;
    },
    validationSchema: validationSchema,
      handleSubmit: (data, { props: { onSubmit } }) => {
        onSubmit(data);
      }
  })(
    ({
      dealer,
      product,
      submittingProduct,
      failSubmitProduct,
      onSubmit,
      setFieldValue,
      values,
      submitForm,
      errors,
      deleteImage,
      touched
    }) => {
      const breakpoint = getBreakpointFromWidth(window.innerWidth);
      const isSmallScreen = ['xs', 'sm'].includes(breakpoint);
      const variantColumns = useMemo(() => {
        const cols = new VariantsColumns();
        return cols.getColumns()
      }, []);
      const variants = _.get(values, 'variants', []);
      const onSubmitVariant = useCallback(
        variant => {
          const nextVariants = [...variants, variant];
          setFieldValue('variants', nextVariants);
        },
        [variants, setFieldValue]
      );
      const onDeleteProductImage = (image) => {
        console.log("hi!", image)
        if (image.id) {
          console.log("request to delete from API")
          console.log(dealer.slug, product.slug, image.id)
          deleteImage(dealer.slug, product.slug, image.id)
          console.log("request sent")
        }
      }
      const SubmitButton = (
        <Fragment>
          <div className='row my-2 my-md-3'>
            <div className='col'>
              <Button
                onClick={() => {
                  submitForm();
                }}
                color='primary'
                fill
                type='button'
              >
                {`${product ? 'Update' : 'Create'} Product`}{' '}
                <Icon icon='paper-plane' />
              </Button>
            </div>
          </div>
          {submittingProduct && <Loading label='Submitting' />}
          {failSubmitProduct && <Alert color='danger'>{failSubmitProduct}</Alert>}
        </Fragment>
      );
      return (
        <Form>
          <div className='row'>
            <div className='col-md-6 col-sm-12 pr-md-1'>
              <div className='row my-2 my-md-3'>
                <div className='col'>
                  <div className='card'>
                    <div className='content'>
                      <Field
                        name='name'
                        label='Title'
                        placeholder='Product Title'
                        component={TextInput}
                      />
                      <Field
                        name='description'
                        label='Description'
                        placeholder='Product Description'
                        type='textarea'
                        style={{ resize: 'none' }}
                        rows='3'
                        component={TextInput}
                      />
                      <Field
                        name='price'
                        label='Price'
                        placeholder='Product Price'
                        component={TextInput}
                      />
                      <Field
                        name='has_tax'
                        number='has_tax'
                        label='Charge taxes on this product'
                        component={CheckboxInput}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='row my-2 my-md-3'>
                {/* variants */}
                <div className='col'>
                  <div className='card'>
                    <div className='content'>
                      <div className='row justify-content-between align-items-center mb-2 mb-md-2'>
                        <div className='col-auto'>
                          <h4 className='title'>
                            <b>Variants</b>
                          </h4>
                        </div>
                        <div className='col-auto'>
                          <AddVariantButton onSubmitVariant={onSubmitVariant} />
                        </div>
                      </div>
                      <div className='row'>
                        <div className='col'>
                          <Table
                            data={variants}
                            NoDataComponent={() => null}
                            columns={variantColumns}
                            pageSize={_.isEmpty(variants) ? 1 : variants.length}
                            showPagination={!_.isEmpty(variants)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {!isSmallScreen && SubmitButton}
            </div>
            <div className='col-md-6 col-sm-12 pl-md-1'>
              <div className='row my-2 my-md-3'>
                <div className='col'>
                  <div className='card '>
                    <div className='content'>
                      <h4>
                        <b>Images</b>
                      </h4>
                      <Field name='images' component={ImagesInput} onDeleteProductImage={onDeleteProductImage} />
                      {errors.images && touched.images && (
                        <p className='text-danger'>{errors.images}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='row my-2 my-md-3'>
                <div className='col'>
                  <div className='card '>
                    <div className='content'>
                      <h4>
                        <b>Categories</b>
                      </h4>
                      <div className='row my-2 my-md-3'>
                        <div className='col'>
                          <Field name='categories' component={CategoryInput} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isSmallScreen && SubmitButton}
            </div>
          </div>
        </Form>
      );
    }
  );

export default ProductForm;
